/* eslint-disable no-param-reassign */
import * as React from 'react';
import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  connectionStates,
  callStates,
} from 'containers/OpVideoPlayer/constants';

import { TimeoutState } from '../components/LiveKitCallManager';
import { timeoutStates, timeoutMs } from '../constants';

// TODO: Update opvideo-state-machine so that states can be imported when isolatedModules is enabled
const VideoStates = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  PREVIEW: 'PREVIEW',
  SURVEILLANCE: 'SURVEILLANCE',
  CALL: 'CALL',
  FAILED: 'FAILED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
  DISCONNECTED: 'DISCONNECTED',
};

export const useDisconnectCallTimeout = (
  timeoutState: React.MutableRefObject<TimeoutState>,
  callState: string,
  shouldResetTimeout: boolean,
  connectionState: string,
  isVideoStateMachineEnabled: unknown,
  currentVideoState: string,
  startTeardown: () => Promise<void>,
  setErrorProps: (props: any) => void,
  setIsLoading: (isLoading: boolean) => void,
  setConnectionState: (state: string) => void,
  setShouldResetTimeout: (state: boolean) => void,
  reconnectToPreview: () => Promise<void>,
) => {
  const { t } = useTranslation();

  const clearTimeoutType = useCallback(
    (timeout: NodeJS.Timeout | null) => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
        timeoutState.current.type = timeoutStates.NO_TIMEOUT;
      }
    },
    [timeoutState],
  );

  const clearTimeouts = useCallback(() => {
    clearTimeoutType(timeoutState.current.previewTimeout);
    clearTimeoutType(timeoutState.current.callTimeout);
    clearTimeoutType(timeoutState.current.disconnectIfNoInteractionTimeout);
  }, [clearTimeoutType, timeoutState]);

  const reconnectAndClearTimeouts = useCallback(async () => {
    await reconnectToPreview();
    clearTimeouts();
  }, [reconnectToPreview, clearTimeouts]);

  const disconnectIfNoInteractionTimeout = useCallback(async () => {
    setConnectionState(connectionStates.DISCONNECTED);
    timeoutState.current.type = timeoutStates.DISCONNECT_WINDOW_EXCEEDED;
    await startTeardown();
    setErrorProps({
      errorMessage: t('Call ended due to inactivity.'),
      buttonContent: t('Reload'),
      handleRetryClick: async () => {
        await reconnectAndClearTimeouts();
      },
    });
  }, [
    setConnectionState,
    timeoutState,
    startTeardown,
    setErrorProps,
    t,
    reconnectAndClearTimeouts,
  ]);

  const startDisconnectIfNoInteractionTimeout = useCallback(() => {
    if (
      timeoutState.current.type === timeoutStates.PREVIEW ||
      timeoutState.current.type === timeoutStates.CALL
    ) {
      timeoutState.current.type = timeoutStates.DISCONNECT_WINDOW;
      timeoutState.current.disconnectIfNoInteractionTimeout = setTimeout(
        disconnectIfNoInteractionTimeout,
        timeoutMs.DISCONNECT,
      );
    }
  }, [timeoutState, disconnectIfNoInteractionTimeout]);

  const handleTimeoutExpiration = useCallback(() => {
    startDisconnectIfNoInteractionTimeout();
    setErrorProps({
      errorMessage: t('Are you still watching?'),
      iconName: 'question circle outline',
      buttonContent: t('Continue'),
      handleRetryClick: () => {
        setErrorProps(null);
        setIsLoading(false);
        clearTimeouts();
        setShouldResetTimeout(true);
      },
    });
  }, [
    startDisconnectIfNoInteractionTimeout,
    setErrorProps,
    t,
    setIsLoading,
    clearTimeouts,
    setShouldResetTimeout,
  ]);

  const isPreviewOrSurveillance = useCallback(() => {
    return isVideoStateMachineEnabled
      ? currentVideoState === VideoStates.PREVIEW ||
          currentVideoState === VideoStates.SURVEILLANCE
      : callState === callStates.PREVIEW;
  }, [isVideoStateMachineEnabled, currentVideoState, callState]);

  const startTimeout = useCallback(() => {
    timeoutState.current.type = isPreviewOrSurveillance()
      ? timeoutStates.PREVIEW
      : timeoutStates.CALL;
    const callOrPreviewTimeout: number = isPreviewOrSurveillance()
      ? timeoutMs.PREVIEW
      : timeoutMs.CALL;

    return setTimeout(handleTimeoutExpiration, callOrPreviewTimeout);
  }, [timeoutState, isPreviewOrSurveillance, handleTimeoutExpiration]);

  const setPreviewOrCallTimeout = useCallback(() => {
    if (
      timeoutState.current.type !== timeoutStates.DISCONNECT_WINDOW &&
      isPreviewOrSurveillance() &&
      !timeoutState.current.previewTimeout
    ) {
      clearTimeouts();
      timeoutState.current.previewTimeout = startTimeout();
      setShouldResetTimeout(false);
    } else if (
      timeoutState.current.type !== timeoutStates.DISCONNECT_WINDOW &&
      !isPreviewOrSurveillance() &&
      !timeoutState.current.callTimeout
    ) {
      clearTimeouts();
      timeoutState.current.callTimeout = startTimeout();
      setShouldResetTimeout(false);
    }
  }, [
    timeoutState,
    isPreviewOrSurveillance,
    clearTimeouts,
    startTimeout,
    setShouldResetTimeout,
  ]);

  useEffect(() => {
    if (
      shouldResetTimeout ||
      (isVideoStateMachineEnabled
        ? [
            VideoStates.PREVIEW,
            VideoStates.SURVEILLANCE,
            VideoStates.CALL,
          ].includes(currentVideoState)
        : connectionState === connectionStates.CONNECTED)
    ) {
      setPreviewOrCallTimeout();
    }
  }, [
    shouldResetTimeout,
    setPreviewOrCallTimeout,
    connectionState,
    isVideoStateMachineEnabled,
    currentVideoState,
  ]);

  // On unmount disconnect from room (if connected)
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);
};
